import { useBillingStore } from '@/stores/billingStore';
import { Sparkles, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function getUrgencyStyle(daysLeft: number | null) {
  if (daysLeft === null || daysLeft > 3) {
    return {
      border: 'border-primary/20',
      bg: 'bg-primary/5',
      icon: Sparkles,
      iconColor: 'text-primary',
      btnClass: 'bg-primary hover:bg-primary/90',
    };
  }
  if (daysLeft > 1) {
    return {
      border: 'border-amber-500/20',
      bg: 'bg-amber-500/5',
      icon: AlertTriangle,
      iconColor: 'text-amber-400',
      btnClass: 'bg-amber-500 hover:bg-amber-600',
    };
  }
  return {
    border: 'border-red-500/20',
    bg: 'bg-red-500/5',
    icon: AlertTriangle,
    iconColor: 'text-red-400',
    btnClass: 'bg-red-500 hover:bg-red-600',
  };
}

export function UpgradeBanner() {
  const billingEnabled = useBillingStore((s) => s.billingEnabled);
  const plan = useBillingStore((s) => s.plan);
  const cancelAtPeriodEnd = useBillingStore((s) => s.cancelAtPeriodEnd);
  const isTrialing = useBillingStore((s) => s.isTrialing);
  const daysLeft = useBillingStore((s) => s.daysLeftInTrial);
  const navigate = useNavigate();

  // Billing switched off — nothing to upgrade to
  if (!billingEnabled) return null;

  // Active pro (not trialing, not cancelling) — no banner
  if (plan === 'pro' && !cancelAtPeriodEnd && !isTrialing()) return null;

  const trialDays = daysLeft();
  const isTrial = isTrialing();
  const urgency = getUrgencyStyle(isTrial ? trialDays : null);
  const Icon = urgency.icon;

  const getMessage = () => {
    if (isTrial) {
      if (trialDays !== null && trialDays <= 0) return <span className="font-medium">Your trial has expired!</span>;
      if (trialDays === 1) return <span className="font-medium">Trial expires tomorrow!</span>;
      if (trialDays !== null && trialDays <= 3) {
        return (
          <>
            Your trial ends in <span className="font-medium">{trialDays} days</span> — add a payment method to keep Pro access.
          </>
        );
      }
      if (trialDays !== null) {
        return (
          <>
            <span className="font-medium">{trialDays} days left</span> in your Pro trial.
          </>
        );
      }
    }
    return (
      <>
        Unlock Zoom account management, reports, and more with{' '}
        <span className="font-medium">Pro</span>.
      </>
    );
  };

  const getCtaText = () => {
    if (isTrial && trialDays !== null && trialDays <= 1) return 'Add payment now';
    if (isTrial) return 'Add payment method';
    return 'Upgrade';
  };

  return (
    <div className={`flex items-center gap-3 rounded-lg border ${urgency.border} ${urgency.bg} px-4 py-2.5`}>
      <Icon className={`h-4 w-4 shrink-0 ${urgency.iconColor}`} />
      <p className="flex-1 text-sm text-foreground">{getMessage()}</p>
      <button
        onClick={() => navigate('/settings?tab=billing')}
        className={`shrink-0 rounded-md px-3 py-1 text-xs font-medium text-white ${urgency.btnClass}`}
      >
        {getCtaText()}
      </button>
    </div>
  );
}
